/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable max-lines */
import { Injectable, Injector } from '@angular/core';
import { FormGroup } from '@angular/forms';
import {
  chain,
  concat,
  eq,
  every,
  filter,
  find,
  forEach,
  get,
  has,
  head,
  includes,
  intersectionBy,
  isEmpty,
  isEqual,
  isNil,
  map,
  merge,
  omit,
  pick,
  reduce,
  set,
  size,
  some,
  unset,
  keys,
  compact,
  isArray,
  assignIn,
} from 'lodash';

import {
  CompanyUtilService,
  DataSource,
  DmDataService,
  SitesService,
  TransferGroupService,
  ValidationService,
} from '@shared/services';
import {
  COMMON_ACTION_TYPES,
  COMMON_ACTION_TYPES_REJECT,
  CreateFormRules,
  ENTITY_TYPES,
  EntityId,
  IApiListResponse,
  IBaseEntityType,
  IBaseFormService,
  IBulkUpdateDocumentsResult,
  IDocumentOperationOptions,
  IEvent,
  IFilterQuery,
  ILimit,
  IModalDialogOptions,
  ISite,
  ITransferData,
  ITransferDestination,
  ITransferGrandTotal,
  ITransferGroupFormFieldPath,
  NotificationType,
  REPLENISH_TYPE_NAMES,
  TRANSFER_PDF_HEADERS,
  TRANSFER_PDF_HEADERS_FINAL,
  TRANSFER_TYPE,
  ITask,
  TooltipIconConfig,
  ReallyChangedTransfers,
  ITaskOptions,
  IWidgetAction,
  EDIT_GRID_ITEM_ACTION_TYPE,
  IPageJumpRouteState,
  DID_KEYWORD_MAPPING,
  WANT_TO_KEYWORD_MAPPING,
  TRANSFER_PDF_HEADER_VALUES,
} from '@shared/types';
import { createFormRules as createTransferGroupFormRules } from './form-rules';
import { lazyInject } from '@app/containers/base/decorators';
import {
  BTN_CANCEL,
  BTN_OK,
  BUTTON_ACTIONS,
} from '@app/text-content/button-templates/button-templates-content';
import { TRANSFER_GROUP_MODULE } from '@app/routes';
import {
  getFormControl,
  getFormFieldValue,
  setFormFieldValue,
} from '@app/components/form/services/schema-form/schema-form-functions';

import { TransferBaseFormService } from '../transfer-common';
import {
  BehaviorSubject,
  combineLatest,
  from,
  iif,
  Observable,
  of,
  Subject,
} from 'rxjs';
import {
  addStoreNumberForSites,
  filterSelectedSites,
  generateBulkUpdateConfirmMessageParam,
  getAutoCompleteTransferNextStatus,
  getEventRelatedAndWarehouseSites,
  isSiteTypePrimaryWarehouse,
  setPrimaryAndWarehouseTop,
  addParQtyColumn,
  addQtyPulledColumn,
} from '../transfer/transfer.util';
import { getSiteSelectorButtonCaption } from '@app/shared/utils/site-selector.utils';
import { getSiteSelectorColumnsDefinition } from '@app/shared/utils/site-selector-columns-definition-utils';
import {
  defaultWidgetData,
  gridSelectModalOptions,
} from '@app/components/xux/xux-edit-grid/xux-edit-grid-widget.settings';
import { getSubtractDateString, toDate } from '@app/shared/utils/date-utils';
import {
  observableCombineLatest,
  observableOf,
  observableThrowError,
  rx,
} from '@app/shared/rx';
import {
  ENTITY_ID_PATH,
  MAX_PAGE_SIZE,
  ONLY_DATE_SUBSTRING_LENGTH,
  SECONDS,
} from '@config/global-constants';
import { isEventType } from '../transfer/transfer-type.utils';
import {
  generateObjectId,
  getObservableValue,
} from '@app/shared/utils/common-utils';
import { calcDepartmentCosts } from './utils/gand-totals.utils';
import { TRANSFER_STATUS_VALUE } from '@app/shared/types/enums/transfer-status.enum';
import { getSiteSelectorMaxLimitDefine } from '@shared/modal-definitions';
import { TOASTER_ERROR_MESSAGE } from '@app/shared/constants';
import { MAX_SITES_LIMIT } from './transfer-group.constants';
import { CHANGE_STATUS_TOTAL_STEPS } from '@shared/services/transfer/transfer-constants';
import {
  XUX_GRID_ACTIONS,
  XuxGridAction,
  ToolbarButtonDefinition,
} from '@geniuspos/ui-shared';
import { XuxEditGridComponent } from '@components/xux/xux-edit-grid/xux-edit-grid-widget.component';
import { InventoryItemVenueService } from '@shared/services/inventory-item-venue';
import {
  composeWithDefaultEventQuery,
  getPreEventTransactionDateRange,
  getTransferEventQueryFilter,
} from '../transfer/transfer.query.util';
import {
  TRANSFER_SAVED_STATUS,
  TRANSFER_UNSAVED_STATUS,
} from '@shared/services/transfer/transfer.definition';
import { getSiteDepartmentCode } from '@app/shared/utils/department-code.utils';
import { getSaveBeforeProcessModal } from './modal-configs';
import { IPdfDefinitions } from '@app/shared/common/make-pdf/v2';
import { TRANSFER_FORM_FIELD_PATHS } from '../transfer/transfer-form-field-paths';
import { HttpErrorResponse } from '@angular/common/http';
import { TaskService } from '../task';
import { generateProgressBarLoadingParams } from '@app/shared/utils/task.utils';
import {
  markTransferItemsAcknowledge,
  unmarkTransferItemsAcknowledge,
  pushNewTransfersToDoc,
} from '@shared/services/transfer-group/utils/transfer-items-utils';
import { getUnsavedChangeInfo } from '@app/shared/utils';
import { takeUntil } from 'rxjs/operators';
import { TransferDestinationsFormService } from '../transfer-destinations/transfer-destinations.form.service';
import { TransferAutoRestockService } from '../transfer-auto-restock/transfer-auto-restock.service';
import { StandWorksheetCalcServiceProvider } from '../stand-worksheet/calculation-services/stand-worksheet-calc-service-provider';
import {
  IAfterReplenishParams,
  IReplenishDestination,
} from '../replenish-worker/replenish.types';
import { DEFAULT_REPLENISH_PERCENT } from '../replenish-worker/replenish.constants';
import { BULK_REPLENISH_CONFIG_MAPPING } from '../transfer/transfer-replenish.constants';
import { I18n } from '@app/i18n/strings-keys';

@Injectable()
export class TransferGroupFormService
  extends TransferBaseFormService
  implements IBaseFormService<ENTITY_TYPES.TRANSFER_GROUP>
{
  public static ENTITY_TYPE: ENTITY_TYPES = ENTITY_TYPES.TRANSFER_GROUP;

  @lazyInject(ValidationService) public validationService: ValidationService;
  @lazyInject(CompanyUtilService) public companyService: CompanyUtilService;
  @lazyInject(SitesService) public sitesService: SitesService;
  @lazyInject(DmDataService) public dmDataService: DmDataService;
  @lazyInject(TransferGroupService)
  public transferGroupService: TransferGroupService;
  @lazyInject(TaskService) public taskService: TaskService;
  @lazyInject(InventoryItemVenueService)
  public inventoryItemVenueService: InventoryItemVenueService;
  @lazyInject(TransferDestinationsFormService)
  public transferDestinationsFormService: TransferDestinationsFormService;
  @lazyInject(TransferAutoRestockService)
  public transferAutoRestockService: TransferAutoRestockService;
  @lazyInject(StandWorksheetCalcServiceProvider)
  public standWorksheetCalcServiceProvider: StandWorksheetCalcServiceProvider;

  public formFieldPath: ITransferGroupFormFieldPath = {
    count: 'count',
    eventId: 'event_id',
    eventName: 'event_name',
    initialCount: 'initial_count',
    inventoryItemVenues: 'inventory_items',
    measureEntityId: 'measure_entity_id',
    measureFactorValue: 'measure_factor_value',
    name: 'name',
    siteId: 'site_id',
    storeNumber: 'store_number',
    siteName: 'site_name',
    siteIdTo: 'site_id_to',
    transactionDate: 'transaction_date',
    transferStatus: 'status',
    transferType: 'transfer_type',
    virtualGrandTotals: 'virtual.grand_totals',
    virtualSelectedEvent: 'virtual.selected_event',
    virtualTransactionDate: 'virtual.transaction_date',
    virtualTransferItems: 'virtual.transfer_items',
    virtualReallyChangedTransfers: 'virtual.really_changed_transfers',
  };

  public createFormRulesFunction: CreateFormRules<TransferGroupFormService> =
    createTransferGroupFormRules;

  public selectedSite: ISite;
  public eventIdDataSource: DataSource;
  public siteIdDataSource: DataSource;
  public transactionDateLimit: ILimit<string | null> = {
    minimumObs: new BehaviorSubject(
      getSubtractDateString(toDate().toISOString(), ONLY_DATE_SUBSTRING_LENGTH),
    ),
    maximumObs: new BehaviorSubject(null),
  };

  public virtualTransferItemsAction$: Subject<IWidgetAction> = new Subject();
  public selectedEvent$: BehaviorSubject<IEvent> = new BehaviorSubject(null);

  public sitesDisplayCount$: BehaviorSubject<number> =
    new BehaviorSubject<number>(0);
  public transferSiteList$: BehaviorSubject<ISite[]> = new BehaviorSubject(
    null,
  );
  public eventList$: Subject<IEvent[]> = new Subject();
  public primaryWarehouseTransferSiteList$: BehaviorSubject<ISite[]> =
    new BehaviorSubject<ISite[]>(null);
  public transferDestinationsDisplayCount$: BehaviorSubject<number> =
    new BehaviorSubject<number>(0);
  public transferDestinationsSelectedCount$: BehaviorSubject<number> =
    new BehaviorSubject<number>(0);
  public transferDestinationsTitleTips$: BehaviorSubject<string> =
    new BehaviorSubject<string>('');
  public transferDestinationsDescription$: BehaviorSubject<string> =
    new BehaviorSubject<string>(
      this.i18nService.instant(
        I18n.Forms.GridMessages.ShowingEntitiesWithSelected,
        {
          count: 0,
          entities: this.i18nService.translate(
            I18n.EntityNames.SiteSingularAndPlural,
          ),
          selectedCount: 0,
        },
      ),
    );
  public siteModalDescription$: BehaviorSubject<string> =
    new BehaviorSubject<string>(
      this.i18nService.instant(I18n.Forms.GridMessages.ShowingEntities, {
        count: 0,
        entities: this.i18nService.instant(
          I18n.EntityNames.siteSingularAndPlural,
        ),
      }),
    );
  public isSiteNotValid$: BehaviorSubject<boolean> = new BehaviorSubject(true);
  public isEventExpired$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  public transferType$: Subject<TRANSFER_TYPE> = new Subject();
  public isOutSideEventTransfer$: Subject<boolean> = new Subject();
  // hide buttons of event is  closed and event date is not valid(date not expired)
  public hideRelatedButtonsOfInvalidEvent$: BehaviorSubject<boolean> =
    new BehaviorSubject(false);
  public dmSyncFinish$: Subject<ITask> = new Subject();

  public transferTypeAndDateLimit$: Observable<
    [TRANSFER_TYPE, string, string]
  > = observableCombineLatest([
    this.transferType$,
    this.transactionDateLimit.minimumObs,
    this.transactionDateLimit.maximumObs,
  ]);

  public transferDateNotEmpty$: Subject<boolean> = new Subject();
  public disableAddSiteObs: Observable<boolean> = observableCombineLatest([
    this.isSiteNotValid$.pipe(rx.map((isSiteNotValid) => !isSiteNotValid)),
    this.transferDateNotEmpty$,
    this.selectedEvent$.pipe(
      rx.map((selectEvent) =>
        isEventType(this.getFormTransferType()) ? !isEmpty(selectEvent) : true,
      ),
    ),
    this.transferSiteList$,
    this.primaryWarehouseTransferSiteList$,
    this.isEventExpired$,
  ]).pipe(
    rx.map(
      ([
        isSiteValid,
        isTransferDateValid,
        isEventValid,
        transferSiteList,
        primaryWarehouseTransferSiteList,
        isEventExpired,
      ]) =>
        !(
          isSiteValid &&
          isTransferDateValid &&
          isEventValid &&
          !(
            isNil(transferSiteList) && isNil(primaryWarehouseTransferSiteList)
          ) &&
          !isEventExpired
        ),
    ),
  );
  public isTransferItemsNotEmpty$: Subject<boolean> = new Subject();

  public isDisableTransferDateObs: Observable<boolean> =
    observableCombineLatest([
      this.isTransferItemsNotEmpty$,
      this.isOutSideEventTransfer$,
      this.selectedEvent$.pipe(
        rx.map(() => this.isDuringEventTransfer()),
        rx.defaultIfEmpty(false),
      ),
    ]).pipe(
      rx.map(
        ([isDestinationNotEmpty, isOutSideEventTransfer, isDuringEvent]) =>
          isDestinationNotEmpty || isOutSideEventTransfer || isDuringEvent,
      ),
    );
  public bulkReplenishToPARDisabled$: Subject<boolean> = new Subject();

  private destinationGrid: XuxEditGridComponent;
  private actionMapperForTransfer: {
    [key: string]: {
      handler: Function;
      did: string;
      wantTo: string;
      dmSync?: boolean;
    };
  } = {
    [COMMON_ACTION_TYPES.Submit]: {
      handler: this.bulkUpdateTransfersStatus.bind(this, null),
      did: DID_KEYWORD_MAPPING.submitted,
      wantTo: WANT_TO_KEYWORD_MAPPING.submit,
      dmSync: true,
    },
    [COMMON_ACTION_TYPES.Cancel]: {
      handler: this.bulkUpdateTransfersStatus.bind(
        this,
        TRANSFER_STATUS_VALUE.Canceled,
      ),
      did: DID_KEYWORD_MAPPING.canceled,
      wantTo: WANT_TO_KEYWORD_MAPPING.cancel,
    },
    [COMMON_ACTION_TYPES.Accept]: {
      handler: this.bulkUpdateTransfersStatus.bind(
        this,
        TRANSFER_STATUS_VALUE.Completed,
      ),
      did: DID_KEYWORD_MAPPING.accepted,
      wantTo: WANT_TO_KEYWORD_MAPPING.accept,
      dmSync: true,
    },
    [COMMON_ACTION_TYPES_REJECT]: {
      handler: this.bulkUpdateTransfersStatus.bind(
        this,
        TRANSFER_STATUS_VALUE.Rejected,
      ),
      did: DID_KEYWORD_MAPPING.rejected,
      wantTo: WANT_TO_KEYWORD_MAPPING.reject,
    },
  };

  public constructor(public injector: Injector) {
    super(injector);
    this.initEventDataSource();
    this.initSiteIdDataSource();
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public onFormReady(form: FormGroup): void {
    // Make sure to get the required data (such as siteList, eventList, selectedEvent) before shutting down the loader.
    // The loader will be shut down later in ensureAllDataIsFetched function。
    this.uiActions.preventDisableLoaderAfterRequestFinish(true);
    this.uiActions.preventLoaderCloseWhenTimeout(true);
    this.initTransferItemsActionSubs();
    this.initTransferItemsSubscription();
    this.initTransferTransferTypeSubscription();

    this.initEventSubscription();
    this.initEventIdSubscription();
    this.initFromSiteSubscription();
    this.ensureAllDataIsFetched();
    this.initTransferDateSubs();

    this.initDescriptionSubscriptions();

    this.eventIdDataSource?.refresh();
    this.siteIdDataSource?.refresh();
    this.getTransferSiteList();
    this.getQohFromVisible();
    this.initRouteSubscription();
  }

  public initTransferItemsActionSubs(): void {
    this.subscriptions.push(
      this.virtualTransferItemsAction$.subscribe((action) => {
        if (
          isEqual(EDIT_GRID_ITEM_ACTION_TYPE.GRID_ITEMS_REMOVED, action.type)
        ) {
          this.handleDestinationGridRemoveRow(action, this.destinationGrid);
        }
      }),
    );
  }

  public initRouteSubscription(): void {
    this.subscriptions.push(
      combineLatest([this.routeState$, this.transferSiteList$.pipe(rx.skip(1))])
        .pipe(rx.distinctUntilChanged())
        .subscribe(([value, siteList]: [IPageJumpRouteState, ISite[]]) => {
          if (!isEmpty(value?.siteIdsTo)) {
            const transferDestinations = filter(siteList, (site) =>
              includes(value?.siteIdsTo, site?.id),
            );
            const additionalItems = map(transferDestinations, (item) => ({
              ...item,
              site_department_code_to: getSiteDepartmentCode(item),
              site_id_to: get(item, 'id'),
              store_number_to: get(item, 'store_number'),
              site_name_to: get(item, 'name'),
              status: TRANSFER_STATUS_VALUE.Null,
              error: false,
            }));

            setFormFieldValue(
              this.form,
              this.formFieldPath.virtualTransferItems,
              additionalItems,
            );
          }
        }),
    );
  }

  public ensureAllDataIsFetched(): void {
    const unsubscribe: Subject<void> = new Subject<void>();
    this.subscriptions.push(
      observableCombineLatest([
        this.eventList$.pipe(rx.startWith(null)),
        this.selectedEvent$,
        this.transferSiteList$,
      ])
        .pipe(takeUntil(unsubscribe))
        .subscribe(
          ([eventList, selectedEvent, siteList]: [
            IEvent[],
            IEvent,
            ISite[],
          ]): void => {
            const isOutsideTransfer = this.isOutsideOfEventTransfer();
            if (
              (isOutsideTransfer ||
                !isNil(eventList) ||
                !isNil(selectedEvent)) &&
              !isNil(siteList)
            ) {
              this.uiActions.preventDisableLoaderAfterRequestFinish(false);
              this.uiActions.preventLoaderCloseWhenTimeout(false);
              this.uiActions.disableLoader();
              unsubscribe.next();
            }
          },
        ),
    );
  }

  public destroyFormDefinition(): void {
    this.selectedEvent$.next(null);
    this.transferSiteList$.next(null);
    this.primaryWarehouseTransferSiteList$.next(null);
    this.isEventExpired$.next(false);
    this.transferDestinationsTitleTips$.next('');
    this.hideRelatedButtonsOfInvalidEvent$.next(false);
    this.uiActions.preventDisableLoaderAfterRequestFinish(false);
    this.uiActions.preventLoaderCloseWhenTimeout(false);
    super.destroyFormDefinition();
  }

  public initTransferItemsSubscription(): void {
    this.subscriptions.push(
      this.fieldValueChanges(this.formFieldPath.virtualTransferItems).subscribe(
        (transferItems) => {
          this.isTransferItemsNotEmpty$.next(!isEmpty(transferItems));
          this.bulkReplenishToPARDisabled$.next(
            isEmpty(transferItems) ||
              every(transferItems, (item) =>
                includes(TRANSFER_SAVED_STATUS, item.status),
              ),
          );

          this.updateGrandTotalsData(transferItems);
          this.generateVoidAllowedMap(transferItems);
        },
      ),
    );
  }

  public initTransferTransferTypeSubscription(): void {
    this.subscriptions.push(
      this.fieldValueChanges(this.formFieldPath.transferType).subscribe(
        (transferType) => {
          this.transferType$.next(transferType);
          this.isOutSideEventTransfer$.next(
            isEqual(TRANSFER_TYPE.OUTSIDE_OF_EVENT_TRANSFER, transferType),
          );
        },
      ),
    );
  }

  public initTransferDateSubs(): void {
    this.subscriptions.push(
      this.fieldValueChanges(this.formFieldPath.transactionDate).subscribe(
        (transferDate) => {
          this.transferDateNotEmpty$.next(!isEmpty(transferDate));
        },
      ),
    );
  }

  public initEventSubscription(): void {
    const subscription = this.selectedEvent$
      .pipe(rx.distinctUntilChanged(), rx.filter(Boolean))
      .subscribe(this.onEventChange.bind(this));
    this.subscriptions.push(subscription);
  }

  public initEventIdSubscription(): void {
    const subscription = observableCombineLatest([
      this.eventList$,
      this.fieldValueChanges(this.formFieldPath.eventId),
    ]).subscribe(([events, eventId]: [IEvent[], string]) => {
      const selectedEvent = find(events, { id: eventId });
      this.selectedEvent$.next(selectedEvent);
      setFormFieldValue(
        this.form,
        this.formFieldPath.virtualSelectedEvent,
        selectedEvent,
        false,
      );
    });
    this.subscriptions.push(subscription);
  }

  public initFromSiteSubscription(): void {
    const subscription = observableCombineLatest([
      this.fieldValueChanges(this.formFieldPath.siteId).pipe(
        rx.distinctUntilChanged(),
      ),
      this.transferSiteList$.pipe(rx.distinctUntilChanged(isEqual)),
      this.selectedEvent$.pipe(rx.distinctUntilChanged()),
    ]).subscribe(([siteId, transferSites, event]): void => {
      if (!isEmpty(siteId) && !isEmpty(transferSites)) {
        this.selectedSite = find(transferSites, (site) =>
          isEqual(site.id, siteId),
        );
        if (
          this.isDuringEventTransfer() &&
          isSiteTypePrimaryWarehouse(this.selectedSite) &&
          !isNil(event)
        ) {
          this.getPrimaryWarehouseTransferSiteList();
        } else {
          this.isSiteNotValid$.next(false);
        }
      } else {
        const primaryWarehouseTransferSiteList = getObservableValue(
          this.primaryWarehouseTransferSiteList$,
        );
        this.isSiteNotValid$.next(
          isEmpty(transferSites) && isEmpty(primaryWarehouseTransferSiteList),
        );
      }

      const fromSite = find(transferSites, { id: siteId });
      setFormFieldValue(
        this.form,
        this.formFieldPath.storeNumber,
        get(fromSite, 'store_number'),
        false,
      );
      setFormFieldValue(
        this.form,
        this.formFieldPath.siteName,
        get(fromSite, 'name'),
        false,
      );
    });
    this.subscriptions.push(subscription);
  }

  public updateGrandTotalsData(data: ITransferDestination[]): void {
    const destinationsWithNewStatus = filter(data, {
      status: TRANSFER_STATUS_VALUE.New,
    });
    const virtualTransferItems = get(
      this.document,
      this.formFieldPath.virtualTransferItems,
      [],
    );
    const needCalculatedItems = concat(
      destinationsWithNewStatus,
      virtualTransferItems,
    );

    const virtualReallyChangedTransfers = getFormFieldValue(
      this.form,
      this.formFieldPath.virtualReallyChangedTransfers,
    );

    forEach(needCalculatedItems, (transferItem) => {
      const siteIdToValue = get(transferItem, this.formFieldPath.siteIdTo);
      const reallyChangedTransfer = get(
        virtualReallyChangedTransfers,
        siteIdToValue,
      );

      if (isEmpty(reallyChangedTransfer?.items)) {
        return;
      }

      merge(
        transferItem,
        get(
          virtualReallyChangedTransfers,
          [get(transferItem, this.formFieldPath.siteIdTo), 'grand_total'],
          {},
        ),
      );
    });

    const departmentTotal: ITransferGrandTotal[] = calcDepartmentCosts(
      needCalculatedItems,
      this.i18nService,
    );

    setFormFieldValue(
      this.form,
      this.formFieldPath.virtualGrandTotals,
      departmentTotal,
      false,
    );
  }

  public isTransferGroupItemsEmpty(): boolean {
    if (!this.form) {
      return true;
    }

    return isEmpty(
      this.getFormFieldValue(this.formFieldPath.virtualTransferItems),
    );
  }

  public async getPrimaryWarehouseTransferSiteList(): Promise<void> {
    this.isSiteNotValid$.next(true);
    this.isTransferItemsNotEmpty$.next(true);
    const sites = getObservableValue(this.transferSiteList$);
    const eventSites = get(
      getObservableValue(this.selectedEvent$),
      'available_sites',
      [],
    );
    const assignedEventSites: ISite[] = intersectionBy(sites, eventSites, 'id');
    const siteFromId = getFormFieldValue(this.form, this.formFieldPath.siteId);
    const siteGroupId = await this.dmDataService
      .getSiteGroupId(map(sites, 'id'))
      .toPromise();
    const sitesPreferences = await this.dmDataService
      .getSitesPreferencesBySiteGroupId(siteGroupId)
      .toPromise();
    const sitePrimaryWarehouseMap = reduce(
      sitesPreferences,
      (result, sitePreference) => {
        if (!isNil(get(sitePreference, 'preferences.back_office.warehouse'))) {
          return {
            ...result,
            [sitePreference.site_id]: get(
              sitePreference,
              'preferences.back_office.warehouse.primary_warehouse',
            ),
          };
        }

        return result;
      },
      {},
    );
    this.primaryWarehouseTransferSiteList$.next(
      filter(assignedEventSites, (site) => {
        return isEqual(get(sitePrimaryWarehouseMap, site.id), siteFromId);
      }),
    );

    this.isSiteNotValid$.next(false);
    this.isTransferItemsNotEmpty$.next(!this.isTransferGroupItemsEmpty());
  }

  public fetchSiteList(
    query: IFilterQuery,
    showOnlyName = false,
  ): Promise<IApiListResponse> {
    if (!this.form) {
      return Promise.resolve({
        items: [],
      } as IApiListResponse<any>);
    }

    const eventIdValue: string = getFormFieldValue(
      this.form,
      this.formFieldPath.eventId,
    );

    const queryWithDefaults: IFilterQuery = merge(
      {
        filter: `is_active eq true`,
        select: [
          'name',
          'id',
          'type',
          'business_type',
          'store_number',
          'metadata',
        ],
        filter_by_assigned_sites: true,
        include_inactive: false,
      },
      query,
      {
        top: MAX_PAGE_SIZE,
      },
    );

    if (isEventType(this.getFormTransferType())) {
      if (isNil(eventIdValue)) {
        return Promise.resolve({
          items: [],
        } as IApiListResponse<any>);
      }
      queryWithDefaults.top = MAX_PAGE_SIZE;
    }

    return observableCombineLatest([
      this.sitesService.fetchList(queryWithDefaults),
      this.selectedEvent$,
    ])
      .pipe(
        rx.first(),
        rx.map(([response, event]) => {
          if (!isEmpty(query) && includes(query.filter, '_id')) {
            if (!showOnlyName) {
              response.items = addStoreNumberForSites(
                response.items as ISite[],
              );
            }

            return response;
          }

          if (
            !this.form ||
            (isNil(event) && isEventType(this.getFormTransferType()))
          ) {
            return { ...response, items: [] };
          }

          const eventSites: ISite[] = get(event, 'available_sites', []);
          let sites: ISite[] = response.items as ISite[];

          if (isEventType(this.getFormTransferType())) {
            sites = getEventRelatedAndWarehouseSites(sites, eventSites);
            response.total = size(sites);
          }
          sites = setPrimaryAndWarehouseTop(sites);

          if (this.isDuringEventTransfer()) {
            const eventIdControl = getFormControl(
              this.form,
              this.formFieldPath.eventId,
            );
            const headElementOfSites = head(sites);
            const hasPrimaryWarehouse =
              isSiteTypePrimaryWarehouse(headElementOfSites);
            if (hasPrimaryWarehouse && eventIdControl.touched) {
              this.selectedSite = headElementOfSites;
              setFormFieldValue(
                this.form,
                this.formFieldPath.siteId,
                get(headElementOfSites, 'id'),
              );
            } else {
              this.isTransferItemsNotEmpty$.next(
                !this.isTransferGroupItemsEmpty(),
              );
            }
          }

          if (!showOnlyName) {
            sites = addStoreNumberForSites(sites);
          }
          response.items = sites;

          return response;
        }),
      )
      .toPromise();
  }

  public siteIdDataSourceFetchList(
    query: IFilterQuery,
  ): Promise<IApiListResponse> {
    return this.fetchSiteList(query).then(
      (result) => result,
      (error) => {
        this.siteIdDataSource.error.next(error);

        return Promise.reject(error);
      },
    );
  }

  public async checkAndHandleEventExpired(
    response: IApiListResponse<IBaseEntityType>,
  ): Promise<IApiListResponse<IBaseEntityType>> {
    const eventId = getFormFieldValue(this.form, this.formFieldPath.eventId);
    const virtualTransferItems = getFormFieldValue(
      this.form,
      this.formFieldPath.virtualTransferItems,
    );
    if (isEmpty(virtualTransferItems)) {
      return response;
    }

    if (isNil(find(response.items, { id: eventId }))) {
      this.isEventExpired$.next(true);

      return this.eventIdDataSource.service
        .fetchList({
          filter: `_id eq objectid(${eventId})`,
          include_inactive: true,
        })
        .pipe(rx.first())
        .toPromise();
    }

    return response;
  }

  public eventDataSourceFetchList(
    query: IFilterQuery,
  ): Promise<IApiListResponse> {
    const transferTypeValue: TRANSFER_TYPE = this.getFormTransferType();

    if (!this.form || !isEventType(transferTypeValue)) {
      return Promise.resolve({
        items: [],
      } as IApiListResponse<any>);
    }

    const userQuery = {
      ...query,
      filter: getTransferEventQueryFilter(transferTypeValue),
    };
    const eventQuery = composeWithDefaultEventQuery(userQuery);

    return this.eventIdDataSource.service
      .fetchList(eventQuery)
      .pipe(
        rx.first(),
        rx.map(async (response: IApiListResponse<IBaseEntityType>) => {
          if (isNil(this.form)) return response;
          const newResponse: IApiListResponse<IBaseEntityType> =
            await this.checkAndHandleEventExpired(response);
          this.eventList$.next(newResponse.items as IEvent[]);

          return newResponse;
        }),
      )
      .toPromise()
      .then(
        (result) => result,
        (error) => (
          this.eventIdDataSource.error.next(error), Promise.reject(error)
        ),
      );
  }

  public initEventDataSource(): void {
    this.eventIdDataSource = this.entityServiceProvider.createDataSource(
      ENTITY_TYPES.EVENTS,
    );
    this.eventIdDataSource.fetchList = this.eventDataSourceFetchList.bind(this);
  }

  public initSiteIdDataSource(): void {
    this.siteIdDataSource = this.entityServiceProvider.createDataSource(
      ENTITY_TYPES.SITE,
    );
    if (this.siteIdDataSource.service) {
      this.siteIdDataSource.service.quickFilterFields = [
        'name',
        'store_number',
      ];
    }
    this.siteIdDataSource.fetchList = this.siteIdDataSourceFetchList.bind(this);
  }

  public initDescriptionSubscriptions(): void {
    this.subscriptions.push(
      observableCombineLatest([
        this.transferDestinationsDisplayCount$,
        this.transferDestinationsSelectedCount$,
      ])
        .pipe(rx.distinctUntilChanged(isEqual))
        .subscribe(
          ([displayedCount, selectedCount]: [number, number]): void => {
            const sitesSubTitle = this.i18nService.instant(
              I18n.Forms.GridMessages.ShowingEntitiesWithSelected,
              {
                count: displayedCount,
                entities: this.i18nService.translate(
                  I18n.EntityNames.SiteSingularAndPlural,
                ),
                selectedCount,
              },
            );
            this.transferDestinationsDescription$.next(sitesSubTitle);
          },
        ),
    );
    this.subscriptions.push(
      this.sitesDisplayCount$.subscribe((count: number): void => {
        const sitesSubTitle = this.i18nService.instant(
          I18n.Forms.GridMessages.ShowingEntities,
          {
            count,
            entities: this.i18nService.instant(
              I18n.EntityNames.siteSingularAndPlural,
            ),
          },
        );

        this.siteModalDescription$.next(sitesSubTitle);
      }),
    );
  }

  public async getTransferSiteList(): Promise<void> {
    const response = await this.fetchSiteList({}, true).then(
      (result) => result,
    );

    this.transferSiteList$.next(response.items as ISite[]);
  }

  public onEventChange(selectedEvent: IEvent): void {
    const eventControl = this.getFormFieldControl(this.formFieldPath.eventId);
    const salesStartDateTimeUTC = head(
      selectedEvent.event_days,
    )?.start_date_time;
    const companyCurrentDate = this.companyService.getCurrentTime();
    const salesStartDate = this.companyService.convertUTCDateWithTimeZone(
      salesStartDateTimeUTC,
      true,
    );
    const isDuringEvent = this.isDuringEventTransfer();
    const isPreEvent = this.isPreEventTypeTransfer();

    if (isPreEvent) {
      const { minimum, maximum } = getPreEventTransactionDateRange(
        companyCurrentDate,
        salesStartDate,
      );

      this.transactionDateLimit.minimumObs.next(minimum);
      this.transactionDateLimit.maximumObs.next(maximum);
    }

    if (isDuringEvent) {
      this.isTransferItemsNotEmpty$.next(true);
      this.transferSiteList$.next(null);
      this.primaryWarehouseTransferSiteList$.next(null);
    }

    setTimeout(() => {
      this.siteIdDataSource?.refresh();
      this.getTransferSiteList();

      if (this.form) {
        if (isDuringEvent) {
          setFormFieldValue(
            this.form,
            this.formFieldPath.virtualTransactionDate,
            salesStartDateTimeUTC,
            false,
          );
        }
        if (eventControl.touched) {
          setFormFieldValue(this.form, this.formFieldPath.siteId, null, false);
          if (!this.isOutsideOfEventTransfer()) {
            setFormFieldValue(
              this.form,
              this.formFieldPath.transactionDate,
              isPreEvent ? companyCurrentDate : salesStartDate,
            );
          }
          if (isDuringEvent) {
            this.getFormFieldControl(
              this.formFieldPath.virtualTransactionDate,
            )?.markAsDirty();
          }
        }
      }
    });
  }

  public getIsHaveNewTransferItems(): boolean {
    return some(
      this.getFormFieldValue(this.formFieldPath.virtualTransferItems),
      (transferItem: ITransferData) =>
        includes(TRANSFER_UNSAVED_STATUS, transferItem.status),
    );
  }

  public get groupFields(): string[] {
    return [
      this.formFieldPath.name,
      this.formFieldPath.eventId,
      this.formFieldPath.siteId,
      this.formFieldPath.transactionDate,
      this.formFieldPath.virtualTransactionDate,
    ];
  }

  public getGroupInfoDirtyFields(): string[] {
    return filter(this.groupFields, (path) => this.getFormFieldDirty(path));
  }

  public async checkAndSaveGroupInfo(
    gridDirtyFields: string[],
  ): Promise<boolean> {
    let checkPassOrSaved = isEmpty(gridDirtyFields);

    try {
      // dirty, need save group info first
      if (!checkPassOrSaved) {
        const modalConfig = getSaveBeforeProcessModal(this.i18nService);
        const confirmedResult = await this.uiActions.confirm(modalConfig);

        if (!confirmedResult.confirmed) {
          return checkPassOrSaved;
        }

        this.uiActions.enableLoader();

        // save group info
        await this.transferGroupService
          .updateEntity(pick(this.form.value, gridDirtyFields), {
            endpoint: this.document._id,
            suppressSuccessMessage: true,
          })
          .pipe(rx.first())
          .toPromise();

        // clear all group dirty status
        forEach(this.groupFields, (fieldPath) => {
          const fieldControl = this.getFormFieldControl(fieldPath);

          fieldControl?.markAsPristine();
          fieldControl?.markAsUntouched();
        });

        this.uiActions.disableLoader();

        checkPassOrSaved = true;
      }
    } catch (error) {
      this.uiActions.disableLoader();

      if (error instanceof ProgressEvent) {
        this.uiActions.showErrorToasterMessage({
          title: this.i18nService.instant(
            I18n.ErrorMessages.APIRequestTimeoutTitle,
          ),
          message: this.i18nService.instant(
            I18n.ErrorMessages.XVIAPIRequestError,
          ),
        });

        return checkPassOrSaved;
      }

      const toast = {
        errors: {
          title: error.title,
          message: error?.meta?.message || '',
        },
      };

      this.uiActions.showErrorToasterMessage(toast);
    }

    return checkPassOrSaved;
  }

  public async editSiteInventoryItems(
    gridItem: ITransferData,
    needDmSync: boolean = true,
  ): Promise<void> {
    const groupDirtyFields = this.getGroupInfoDirtyFields();
    const isPendingOrOpenTransfer = [
      TRANSFER_STATUS_VALUE.Open,
      TRANSFER_STATUS_VALUE.Pending,
    ].includes(gridItem.status);

    const checkGroupSaveInfoResult =
      await this.checkAndSaveGroupInfo(groupDirtyFields);

    if (!checkGroupSaveInfoResult) {
      return;
    }

    const routeUri = `${TRANSFER_GROUP_MODULE}/${this.document._id}/${
      includes(TRANSFER_UNSAVED_STATUS, gridItem.status) ? 'add' : gridItem._id
    }?get_updated_items=${needDmSync ? isPendingOrOpenTransfer : false}`;

    const siteIdValue = getFormFieldValue(this.form, this.formFieldPath.siteId);
    const isDuringEventTransferAndFromSiteIsPrimaryWarehouse =
      this.isDuringEventTransfer() &&
      isSiteTypePrimaryWarehouse(this.selectedSite);
    const transferSitesList: ISite[] = await (
      isDuringEventTransferAndFromSiteIsPrimaryWarehouse
        ? this.primaryWarehouseTransferSiteList$
        : this.transferSiteList$
    )
      .first()
      .map((sites) => filter(sites, (site) => !isEqual(site.id, siteIdValue)))
      .toPromise();

    this.uiActions.removeNotificationsOnPageLeave();

    this.uiActions.openRoute(routeUri, {
      state: {
        isFormDirty: this.form.dirty,
        // when go to next page, will omit these field to get the new inventory item data
        // when back to this page, will re-call replenish api to get thresholds data
        selectedSite: omit(gridItem, [
          'inventory_items',
          'noAvailablePAR',
          'skipReplenish',
        ]),
        transferSiteList: transferSitesList,
        isEventExpired: getObservableValue(this.isEventExpired$),
        storeForm: merge(
          {},
          {
            site_department_code_to: gridItem.site_department_code_to,
            site_name_to: gridItem.site_name_to,
            site_id_to: gridItem.site_id_to,
            status: gridItem.status,
            transfer_group_entity_id: this.document.entity_id,
            virtual: {
              transfer_group_name: this.getFormFieldValue(
                this.formFieldPath.name,
              ),
              transfer_items: map(
                getFormFieldValue(
                  this.form,
                  this.formFieldPath.virtualTransferItems,
                ),
                (transfer) =>
                  omit(transfer, [
                    'inventory_items',
                    'noAvailablePAR',
                    'skipReplenish',
                  ]),
              ),
            },
          },
          omit(this.form.value, ['virtual.transfer_items']),
        ),
      },
    });
  }

  public clearUnsavedChangesBySaveResult(
    bulkOperationResult: IBulkUpdateDocumentsResult<IBaseEntityType>,
  ): void {
    const savedDocuments = compact(
      concat(
        bulkOperationResult.created_documents,
        bulkOperationResult.updated_documents,
      ),
    );

    if (isEmpty(savedDocuments)) {
      return;
    }

    const transferItems: ITransferData[] = this.getFormFieldValue(
      this.formFieldPath.virtualTransferItems,
    );

    const siteIds = compact(
      map(savedDocuments, (savedDoc) =>
        has(savedDoc, 'site_id_to')
          ? savedDoc.site_id_to
          : find(
              transferItems,
              (virtualTransfer: ITransferData) =>
                virtualTransfer._id === savedDoc._id,
            )?.site_id_to,
      ),
    );

    return this.clearUnsavedChanges(siteIds);
  }

  public clearUnsavedChanges(siteIds: string[]): void {
    if (isEmpty(siteIds)) {
      return;
    }

    const reallyChangedTransfers: ReallyChangedTransfers =
      this.getFormFieldValue(this.formFieldPath.virtualReallyChangedTransfers);

    if (isEmpty(reallyChangedTransfers)) {
      return;
    }

    const transferItemsData: ITransferData[] = this.getFormFieldValue(
      this.formFieldPath.virtualTransferItems,
    );

    forEach(reallyChangedTransfers, (_, key) => {
      if (!includes(siteIds, key)) {
        return;
      }

      unset(reallyChangedTransfers, key);

      set(find(transferItemsData, { site_id_to: key }), 'isChanged', false);
    });

    setFormFieldValue(
      this.form,
      this.formFieldPath.virtualReallyChangedTransfers,
      reallyChangedTransfers,
      false,
    );
    setFormFieldValue(
      this.form,
      this.formFieldPath.virtualTransferItems,
      transferItemsData,
      false,
    );

    const unsavedChangeInfo = getUnsavedChangeInfo(
      this.getFormFieldValue<ITransferData[]>(
        this.formFieldPath.virtualTransferItems,
      ),
      {
        i18nService: this.i18nService,
      },
    );

    this.transferDestinationsTitleTips$.next(unsavedChangeInfo.unsavedMessage);

    if (
      isEqual(unsavedChangeInfo.unsavedCount, 0) &&
      !this.getFormFieldDirty(this.formFieldPath.virtualTransferItems) &&
      isEmpty(this.getGroupInfoDirtyFields())
    ) {
      // form not empty, set form markAsPristine
      this.form.markAsPristine();
    }
  }

  public beforeUpdateAction(
    items: ITransferData[],
    action: string,
  ): Promise<IBulkUpdateDocumentsResult> {
    const defaultUpdateResult: IBulkUpdateDocumentsResult = {
      successCount: 0,
      totalCount: 0,
      updated_documents: [],
    };

    if (
      isEmpty(
        this.getFormFieldValue(
          this.formFieldPath.virtualReallyChangedTransfers,
        ),
      )
    ) {
      return Promise.resolve(defaultUpdateResult);
    }

    const actionHandlerMapping = {
      [COMMON_ACTION_TYPES.Cancel]: (): Promise<IBulkUpdateDocumentsResult> => {
        return Promise.resolve(
          merge(defaultUpdateResult, {
            updated_documents: filter(items, {
              status: TRANSFER_STATUS_VALUE.Open,
            }),
          }),
        );
      },
    };

    if (has(actionHandlerMapping, action)) {
      return actionHandlerMapping[action](items);
    }

    return Promise.resolve(defaultUpdateResult);
  }

  public async bulkUpdateTransfer(
    gridItem: ITransferData[],
    action: string,
    refreshHistory: Function,
    handleTransferGroup: boolean = false,
  ): Promise<boolean> {
    let beUpdatedItems = gridItem;
    const isMultiple = beUpdatedItems.length > 1 || handleTransferGroup;
    const currentAction = this.actionMapperForTransfer[action];
    const needCreateTransfers = this.getNeedCreateTransfers(beUpdatedItems);

    beUpdatedItems = isEqual(action, COMMON_ACTION_TYPES.Cancel)
      ? beUpdatedItems
      : this.combineChangesWithOriginItems(
          beUpdatedItems,
          this.getFormFieldValue(
            this.formFieldPath.virtualReallyChangedTransfers,
          ),
        );
    if (action === COMMON_ACTION_TYPES_REJECT) {
      beUpdatedItems =
        this.transferService.getPreRejectTransfers(beUpdatedItems);
    }

    if (currentAction.dmSync && !isMultiple && isEmpty(needCreateTransfers)) {
      const syncResult: ITask = await this.taskService
        .getDmSyncResult(beUpdatedItems, { isSync: true })
        .pipe(
          rx.catchError((error: HttpErrorResponse) => {
            this.uiActions.showToasterMessage({
              type: NotificationType.ERROR,
              title: TOASTER_ERROR_MESSAGE.title,
              body: this.i18nService.instant(
                I18n.ErrorMessages.CheckInternetAndTryAgain,
              ),
            });

            return observableThrowError(error);
          }),
        )
        .toPromise();
      const subTask: any = head(syncResult?.sub_tasks || []);
      if (
        isEqual('completed', subTask.status) &&
        subTask.value.is_acknowledge
      ) {
        await this.editSiteInventoryItems(beUpdatedItems[0], false);

        return false;
      }
    }

    const message = isMultiple
      ? this.i18nService.instant(
          I18n.ConfirmMessages.AreYouWantToBulkUpdateHowManyTarget,
          generateBulkUpdateConfirmMessageParam({
            action,
            did: null,
            count: beUpdatedItems.length,
            target: I18n.Forms.TransferForm.transfersBrackets,
            i18nService: this.i18nService,
          }),
        )
      : this.i18nService.instant(
          I18n.ConfirmMessages.AreYouWantToBulkUpdateTarget,
          {
            target: this.i18nService.instant(I18n.Forms.TransferForm.transfer),
            wantTo: this.i18nService.instant(action),
          },
        );

    const result = await this.uiActions.confirm({
      text: message,
    });
    if (!result.confirmed) {
      return false;
    }

    await this.beforeUpdateAction(beUpdatedItems, action);

    let bulkCreateResult: IBulkUpdateDocumentsResult;
    if (
      isEqual(action, COMMON_ACTION_TYPES.Submit) &&
      !isEmpty(needCreateTransfers)
    ) {
      bulkCreateResult = await this.bulkSaveTransfers(
        needCreateTransfers,
        true,
      );
      beUpdatedItems = map(beUpdatedItems, (item) => {
        const newItemDocument = find(bulkCreateResult.created_documents, {
          site_id_to: item.site_id_to,
        });
        if (!isNil(newItemDocument)) {
          // unset inventory items to avoid save when before change status
          unset(item, 'inventory_items');
          item._id = newItemDocument._id;
        }

        return item;
      });
      const data = getFormFieldValue(
        this.form,
        this.formFieldPath.virtualTransferItems,
      );
      const newData = map(data, (item) => {
        const newItemDocument = find(bulkCreateResult.created_documents, {
          site_id_to: item.site_id_to,
        });
        let newItem = item;
        if (!isNil(newItemDocument)) {
          newItem = merge(
            {},
            item,
            omit(newItemDocument, ['type', 'store_number']),
          );

          newItem = omit(newItem, ['inventory_items']);

          // need call this for grand total calc(grand total depend on document transfers)
          pushNewTransfersToDoc(this.document, [newItem]);

          return newItem;
        }

        return newItem;
      });
      setFormFieldValue(
        this.form,
        this.formFieldPath.virtualTransferItems,
        newData,
        false,
      );
      if (!eq(bulkCreateResult.totalCount, bulkCreateResult.successCount)) {
        this.clearUnsavedChangesBySaveResult(bulkCreateResult);
        refreshHistory();
        await this.refreshGrandTotal();
        this.updateSpecifiedTransferItemsStatus(
          bulkCreateResult.created_documents,
        );

        this.transferGroupService.shownUpdateMessage(
          {
            did: DID_KEYWORD_MAPPING.saved,
            wantTo: WANT_TO_KEYWORD_MAPPING.save,
            isSingle: !isMultiple,
          },
          bulkCreateResult,
        );

        return;
      }
    }

    if (isMultiple) {
      this.uiActions.enableLoader(
        generateProgressBarLoadingParams({ i18nService: this.i18nService }),
      );
      this.uiActions.preventLoaderCloseWhenTimeout(true);
    }

    const isAccept = isEqual(action, COMMON_ACTION_TYPES.Accept);
    if (isAccept) {
      this.uiActions.preventDisableLoaderAfterRequestFinish(isAccept);
      this.uiActions.preventLoaderCloseWhenTimeout(isAccept);
      this.uiActions.enableLoader();
    }

    iif(
      () => isMultiple && currentAction.dmSync,
      of(true).pipe(
        rx.mergeMap(() =>
          this.taskService
            .getDmSyncResult(beUpdatedItems, {
              noLoader: true,
              stepCount: CHANGE_STATUS_TOTAL_STEPS,
            })
            .pipe(
              rx.mergeMap((taskResult: ITask) => {
                const dmSyncTransferIds = map(
                  filter(map(taskResult.sub_tasks, 'value'), 'is_acknowledge'),
                  'transfer_id',
                );

                markTransferItemsAcknowledge(
                  dmSyncTransferIds,
                  this.form,
                  this.formFieldPath,
                );

                return currentAction.handler(
                  filter(
                    beUpdatedItems,
                    (transfer: ITransferData) =>
                      !dmSyncTransferIds.includes(get(transfer, '_id')),
                  ),
                  {
                    noLoader: true,
                    currentStep: CHANGE_STATUS_TOTAL_STEPS,
                    stepCount: CHANGE_STATUS_TOTAL_STEPS,
                  },
                );
              }),
              rx.map((processResult: IBulkUpdateDocumentsResult) => {
                return {
                  ...processResult,
                  totalCount: size(beUpdatedItems),
                };
              }),
            ),
        ),
      ),
      of(true).pipe(
        rx.mergeMap(() =>
          currentAction.handler(beUpdatedItems, {
            isSync: !isMultiple,
            noLoader: isEqual(action, COMMON_ACTION_TYPES.Accept) || isMultiple,
          }),
        ),
        rx.tap(({ updated_documents }) => {
          unmarkTransferItemsAcknowledge(
            map(updated_documents, '_id'),
            this.form,
            this.formFieldPath,
          );
        }),
      ),
    )
      .pipe(
        rx.mergeMap((bulkUpdateResult: IBulkUpdateDocumentsResult) => {
          return !isAccept
            ? of(bulkUpdateResult)
            : from(
                this.generateVoidAllowedMap(
                  this.getUpdateItem(bulkUpdateResult),
                ),
              ).pipe(rx.map(() => bulkUpdateResult));
        }),
        rx.catchError((error: HttpErrorResponse) => {
          this.uiActions.showToasterMessage({
            type: NotificationType.ERROR,
            title: TOASTER_ERROR_MESSAGE.title,
            body: this.i18nService.instant(
              I18n.ErrorMessages.CheckInternetAndTryAgain,
            ),
          });

          return observableThrowError(error);
        }),
      )
      .subscribe((bulkUpdateResult: IBulkUpdateDocumentsResult) => {
        this.clearUnsavedChangesBySaveResult(bulkUpdateResult);
        refreshHistory();

        const data = getFormFieldValue(
          this.form,
          this.formFieldPath.virtualTransferItems,
        );

        const newData = map(data, (item) => {
          return {
            ...item,
            status:
              find(bulkUpdateResult.updated_documents, { _id: item._id })
                ?.status ?? item.status,
          };
        });

        this.refreshGrandTotal({ noLoader: isMultiple });

        this.transferGroupService.shownUpdateMessage(
          {
            did: currentAction.did,
            wantTo: currentAction.wantTo,
            isSingle: !isMultiple,
          },
          bulkUpdateResult,
        );

        setFormFieldValue(
          this.form,
          this.formFieldPath.virtualTransferItems,
          newData,
          false,
        );
        if (isAccept) {
          this.uiActions.disableLoader();
          this.uiActions.preventDisableLoaderAfterRequestFinish(false);
          this.uiActions.preventLoaderCloseWhenTimeout(false);
        }
        if (isMultiple) {
          this.uiActions.disableLoader();
        }
      });
  }

  public getUpdateItem(
    bulkUpdateResult: IBulkUpdateDocumentsResult,
  ): ITransferData[] {
    const data = getFormFieldValue(
      this.form,
      this.formFieldPath.virtualTransferItems,
    );

    return map(data, (item) => {
      return {
        ...item,
        status:
          find(bulkUpdateResult.updated_documents, { _id: item._id })?.status ??
          item.status,
      };
    });
  }

  public async bulkSaveTransfers(
    transferDestinations: ITransferData[],
    noLoader: boolean = false,
  ): Promise<IBulkUpdateDocumentsResult> {
    if (!noLoader) {
      this.uiActions.enableLoader();
      this.uiActions.preventLoaderCloseWhenTimeout(true);
    }
    const newDocument = Object.assign({}, this.form.value);
    const requestFields = [
      'event_id',
      'inventory_items',
      'site_id',
      'site_id_to',
      'status',
      'transaction_date',
      'virtual.transaction_date',
      'transaction_id',
      'transfer_type',
      'type',
      'transfer_group_entity_id',
    ];
    const requestDocuments = map(transferDestinations, (item) => {
      const newEntityId = generateObjectId();
      const newItem = pick(newDocument, requestFields);
      set(newItem, 'inventory_items', item.inventory_items);
      set(newItem, 'site_id_to', item.id);
      set(newItem, 'transaction_id', newEntityId);
      set(newItem, 'type', ENTITY_TYPES.TRANSFER);
      set(
        newItem,
        'transfer_group_entity_id',
        get(this.document, ENTITY_ID_PATH),
      );
      set(newItem, 'status', TRANSFER_STATUS_VALUE.Null);

      return newItem;
    });

    return this.transferService
      .bulkCreateEntity(requestDocuments, {})
      .pipe(
        rx.finalize(() => {
          if (!noLoader) {
            this.uiActions.disableLoader();
          }
        }),
      )
      .toPromise();
  }

  public updateSpecifiedTransferItemsStatus(
    transfers: IBaseEntityType[],
  ): void {
    const virtualTransferItems: ITransferData[] = getFormFieldValue(
      this.form,
      this.formFieldPath.virtualTransferItems,
    );

    const newData = map(virtualTransferItems, (item) => {
      const newTransfer = find(transfers, { _id: item._id });

      // new Transfer status is same as completed, need disable void
      if (isEqual(TRANSFER_STATUS_VALUE.Completed, newTransfer?.status)) {
        set(this.voidAllowedMap, item._id, false);
      }

      return {
        ...item,
        status: newTransfer?.status ?? item.status,
      };
    });

    setFormFieldValue(
      this.form,
      this.formFieldPath.virtualTransferItems,
      newData,
      false,
    );
  }

  public async bulkVoidTransfer(
    refreshHistory: Function,
    gridItem: ITransferData[],
    enableSingleVoid = true,
  ): Promise<void> {
    const bulkVoidResult: IBulkUpdateDocumentsResult =
      await this.transferGroupService.bulkVoidHandlerForGroup(
        gridItem,
        enableSingleVoid,
      );
    if (!isEmpty(bulkVoidResult?.updated_documents)) {
      refreshHistory();
      this.refreshGrandTotal();
      this.updateSpecifiedTransferItemsStatus(bulkVoidResult.updated_documents);
    }
  }

  public bulkUpdateTransfersStatus(
    defaultTargetStatus: TRANSFER_STATUS_VALUE,
    needSubmitTransfers: ITransferData[],
    options?: ITaskOptions,
  ): Observable<IBulkUpdateDocumentsResult> {
    const updateTransferStatus = getAutoCompleteTransferNextStatus(
      this.getFormTransferType(),
      TRANSFER_STATUS_VALUE.Open,
      this.stateService,
    );
    const targetStatus = defaultTargetStatus || updateTransferStatus;

    return this.transferGroupService.bulkUpdateStatus(
      targetStatus,
      needSubmitTransfers,
      options,
    );
  }

  public async openSiteSelector(): Promise<void> {
    const groupDirtyFields = this.getGroupInfoDirtyFields();

    const checkGroupSaveInfoResult =
      await this.checkAndSaveGroupInfo(groupDirtyFields);

    if (!checkGroupSaveInfoResult) {
      return;
    }
    const availableSitesForEvent = getObservableValue(this.transferSiteList$);
    const siteIdValue = getFormFieldValue(this.form, this.formFieldPath.siteId);
    if (
      isNil(
        find(availableSitesForEvent, (site) => isEqual(site.id, siteIdValue)),
      )
    ) {
      this.uiActions.showToasterMessage({
        type: NotificationType.WARNING,
        body: this.i18nService.instant(
          I18n.ErrorMessages.EventNoAssignmentSiteError,
        ),
        timeout: 10 * SECONDS,
      });

      return;
    }

    const virtualTransferItems = getFormFieldValue(
      this.form,
      this.formFieldPath.virtualTransferItems,
    );
    const transferDestinationIds = map(virtualTransferItems, 'site_id_to');

    const isDuringEventTransferAndFromSiteIsPrimaryWarehouse =
      this.isDuringEventTransfer() &&
      isSiteTypePrimaryWarehouse(this.selectedSite);
    const transferSitesList$: Observable<ISite[]> = (
      isDuringEventTransferAndFromSiteIsPrimaryWarehouse
        ? this.primaryWarehouseTransferSiteList$
        : this.transferSiteList$
    )
      .first()
      .map((sites) => {
        const transferItems = getFormFieldValue(
          this.form,
          this.formFieldPath.virtualTransferItems,
        );

        return filterSelectedSites(
          [siteIdValue, ...map(transferItems, this.formFieldPath.siteIdTo)],
          sites,
        );
      });
    const transferSitesListPromise: ISite[] =
      await transferSitesList$.toPromise();
    const entityIdsForMetadataList: EntityId[] = chain(transferSitesListPromise)
      .flatMap('metadata')
      .map('entity_id')
      .value();

    const siteSelectorGridSettings: IModalDialogOptions = {
      title: I18n.Common.SiteSelector,
      subTitle: this.siteModalDescription$,
      modalClass: `modal-lg`,
      buttons: [
        {
          ...BTN_OK,
          caption: (selectedModalSites) =>
            getSiteSelectorButtonCaption({
              ...selectedModalSites,
              i18nService: this.i18nService,
            }),
          class: 'pull-right btn-primary',
        },
        BTN_CANCEL,
      ],
      schema: {
        properties: {
          modified_message: { type: 'string' },
          items: { type: 'array', required: true },
        },
      },
      formRules: {
        fields: {
          items: {
            ignoreAutoTitleWhenTranslate: true,
            widgetData: {
              displayCountObs: this.sitesDisplayCount$,
              valueField: 'id',
              treeViewMode: true,
              treeChildrenPropertyName: 'children',
              treeColumnTitle: 'hierarchy',
              defaultExpanded: false,
              ignoreItemValueOptimization: true,
              useVisibleItemCountDescription: true,
              searchPlaceholder: I18n.Common.Search,
              columns: getSiteSelectorColumnsDefinition(
                entityIdsForMetadataList,
              ),
              userGridOptions: {
                overlayNoRowsTemplate: I18n.Common.NoRowsToShow,
              },
            },
          },
        },
      },
    };

    const modalOptions: IModalDialogOptions = merge(
      {},
      gridSelectModalOptions(transferSitesList$, this.i18nService),
      getSiteSelectorMaxLimitDefine(
        MAX_SITES_LIMIT,
        transferDestinationIds,
        this.i18nService.instant(I18n.InfoMessages.YouCanSelectUpToTargets, {
          maxCount: MAX_SITES_LIMIT,
          targetsOf: this.i18nService.instant(
            I18n.Forms.TransferForm.sitesAsTransferDestinations,
          ),
          alreadyAdded: `${size(transferDestinationIds)} ${this.i18nService.instant(I18n.EntityNames.siteSingularAndPlural)}`,
        }),
      ),
      defaultWidgetData,
      siteSelectorGridSettings,
    );

    const { confirmed, value } =
      await this.uiActions.formModalDialog(modalOptions);
    if (!confirmed) return;

    const additionalItems = map(value.items, (item) => ({
      ...item,
      site_department_code_to: getSiteDepartmentCode(item),
      site_id_to: get(item, 'id'),
      store_number_to: get(item, 'store_number'),
      site_name_to: get(item, 'name'),
      status: TRANSFER_STATUS_VALUE.Null,
      error: false,
    }));

    const primaryAndWarehouseTopSites: ISite[] = concat(
      additionalItems,
      virtualTransferItems,
    );

    setFormFieldValue(
      this.form,
      this.formFieldPath.virtualTransferItems,
      primaryAndWarehouseTopSites,
    );
  }

  public composeConfirmationModalOptions(action: string): IModalDialogOptions {
    if (action === BUTTON_ACTIONS.REMOVE) {
      return {
        text: this.i18nService.instant(
          I18n.InfoMessages.ConfirmationModalRemovesSites,
        ),
      };
    }
  }

  public getOpenedTransfers(
    items: ITransferData[] = getFormFieldValue(
      this.form,
      this.formFieldPath.virtualTransferItems,
    ),
  ): any {
    return filter(items, (destination) =>
      isEqual(destination.status, TRANSFER_STATUS_VALUE.Open),
    );
  }

  /**
   * get all need update transfers
   * will return all open/pending changed transfers and will return [] when there is no any changes
   *
   * @param containsStatus only include transfer status
   */
  public getNeedUpdateTransfers(
    containsStatus?: TRANSFER_STATUS_VALUE | TRANSFER_STATUS_VALUE[],
  ): ITransferData[] {
    const includeStatusList = compact(
      isArray(containsStatus) ? containsStatus : [containsStatus],
    );

    const reallyChangedTransfers: ReallyChangedTransfers =
      this.getFormFieldValue(this.formFieldPath.virtualReallyChangedTransfers);

    if (isEmpty(reallyChangedTransfers)) {
      return [];
    }

    const reallyChangedSiteIdToList = keys(reallyChangedTransfers);
    const allTransfers: ITransferData[] = this.getFormFieldValue(
      this.formFieldPath.virtualTransferItems,
    );

    const changedTransfers = filter(allTransfers, (transfer) => {
      const siteIdTo = transfer.site_id_to;

      const isSavedTransfer = !includes(
        TRANSFER_UNSAVED_STATUS,
        transfer.status,
      );
      const isIncludeTransfer = isEmpty(includeStatusList)
        ? true
        : includes(includeStatusList, transfer.status);
      const isChangedTransfer = includes(reallyChangedSiteIdToList, siteIdTo);

      return isIncludeTransfer && isSavedTransfer && isChangedTransfer;
    });

    return this.combineChangesWithOriginItems(
      changedTransfers,
      reallyChangedTransfers,
    );
  }

  /**
   * combine local unsaved changes and original items to transfers
   *
   * @param targetTransfers target transfers
   * @param reallyChangedTransfers local unsaved changes(with original items)
   */
  public combineChangesWithOriginItems(
    targetTransfers: ITransferData[],
    reallyChangedTransfers: ReallyChangedTransfers,
  ): ITransferData[] {
    if (isEmpty(targetTransfers) || isEmpty(reallyChangedTransfers)) {
      return targetTransfers;
    }

    return map(targetTransfers, (transfer) => {
      const siteIdTo = transfer.site_id_to;
      const changedTransfer = get(reallyChangedTransfers, siteIdTo, {});
      const changedInventoryItems = get(changedTransfer, 'items', []);
      const changedInventoryItemIds = map(
        changedInventoryItems,
        'item_entity_id',
      );
      const originInventoryItems = get(changedTransfer, 'originItems', []);

      const notChangedInventoryItems = filter(
        originInventoryItems,
        (originItem) => {
          return !includes(changedInventoryItemIds, originItem.item_entity_id);
        },
      );

      const inventoryItems = isEmpty(get(changedTransfer, 'originItems', []))
        ? changedInventoryItems
        : concat(
            notChangedInventoryItems,
            map(changedInventoryItems, (changedItem) => {
              const originItem = find(originInventoryItems, {
                item_entity_id: changedItem.item_entity_id,
              });

              return {
                ...(originItem ?? {}),
                ...changedItem,
              };
            }),
          );

      return assignIn({}, transfer, {
        inventory_items: inventoryItems,
      });
    });
  }

  public getNeedAcceptTransfers(
    items: ITransferData[] = getFormFieldValue(
      this.form,
      this.formFieldPath.virtualTransferItems,
    ),
  ): ITransferData[] {
    return this.isPreEventTypeTransfer()
      ? []
      : filter(items, (destination) =>
          isEqual(destination.status, TRANSFER_STATUS_VALUE.Pending),
        );
  }

  public getNeedRejectTransfers(
    items: ITransferData[] = getFormFieldValue(
      this.form,
      this.formFieldPath.virtualTransferItems,
    ),
  ): ITransferData[] {
    return filter(items, (destination) =>
      isEqual(destination.status, TRANSFER_STATUS_VALUE.Pending),
    );
  }

  public getNeedCreateTransfers(
    items: ITransferData[] = getFormFieldValue(
      this.form,
      this.formFieldPath.virtualTransferItems,
    ),
  ): ITransferData[] {
    const newTransfers = filter(items, (destination) =>
      isEqual(destination.status, TRANSFER_STATUS_VALUE.New),
    );

    return this.combineChangesWithOriginItems(
      newTransfers,
      this.getFormFieldValue(this.formFieldPath.virtualReallyChangedTransfers),
    );
  }

  public getNeedSubmitTransfers(
    items: ITransferData[] = getFormFieldValue(
      this.form,
      this.formFieldPath.virtualTransferItems,
    ),
  ): ITransferData[] {
    const needSubmitTransfers = filter(items, (destination) =>
      isEqual(destination.status, TRANSFER_STATUS_VALUE.Open),
    );

    return this.combineChangesWithOriginItems(
      needSubmitTransfers,
      this.getFormFieldValue(this.formFieldPath.virtualReallyChangedTransfers),
    );
  }

  public getNeedVoidTransfers(
    items: ITransferData[] = getFormFieldValue(
      this.form,
      this.formFieldPath.virtualTransferItems,
    ),
  ): any {
    return filter(items, (destination) =>
      isEqual(destination.status, TRANSFER_STATUS_VALUE.Completed),
    );
  }

  public getUnsavedDestinations(): ITransferDestination[] {
    const virtualTransferDestinations: ITransferDestination[] =
      getFormFieldValue(this.form, this.formFieldPath.virtualTransferItems);

    return filter(virtualTransferDestinations, (item) =>
      includes(TRANSFER_UNSAVED_STATUS, item.status),
    );
  }

  public getEventForReplenish(): object {
    return {
      event: getObservableValue(this.selectedEvent$),
    };
  }

  public getUnsavedDataForReplenish(): object {
    const unSavedData: ReallyChangedTransfers = this.getFormFieldValue(
      this.formFieldPath.virtualReallyChangedTransfers,
    );

    return {
      unSavedData,
    };
  }

  public beforeReplenish(): void {
    this.destinationGrid.xuxGridComponent.gridApi.setFilterModel(null);
  }

  public afterReplenish(params: IAfterReplenishParams): void {
    const destinations = get(
      params,
      'destinations',
      [],
    ) as IReplenishDestination[];
    const reallyChangedTransfers: ReallyChangedTransfers = reduce(
      destinations,
      (changedTransfers, destination) => {
        if (
          isNil(destination.reallyChangedItems) ||
          isEmpty(destination.reallyChangedItems)
        ) {
          return changedTransfers;
        }

        changedTransfers[destination.toSite] = {
          items: destination.reallyChangedItems,
        };

        unset(destination, 'reallyChangedItems');

        return changedTransfers;
      },
      {},
    );

    const originReallyChangedTransfer = getFormFieldValue(
      this.form,
      this.formFieldPath.virtualReallyChangedTransfers,
    );

    forEach(reallyChangedTransfers, (singleTransfer, field) => {
      if (!isEmpty(originReallyChangedTransfer[field])) {
        const mergedData = originReallyChangedTransfer[field].items;

        forEach(reallyChangedTransfers[field].items, (newItem) => {
          const targetItem = find(mergedData, {
            entity_id: newItem.entity_id,
          });
          if (!isNil(targetItem)) {
            merge(targetItem, newItem);
          } else {
            mergedData.push(newItem);
          }
        });
        originReallyChangedTransfer[field].items = mergedData;
      } else {
        originReallyChangedTransfer[field] = {
          ...singleTransfer,
        };
      }
    });

    const virtualTransferItems: any[] = this.getFormFieldValue(
      this.formFieldPath.virtualTransferItems,
    );

    forEach(destinations, (destination) => {
      const virtualTransferItem = find(virtualTransferItems, {
        id: destination.toSite,
      });

      if (!isNil(virtualTransferItem)) {
        merge(virtualTransferItem, destination);
        set(
          virtualTransferItem,
          'inventory_items',
          destination.inventory_items,
        );
      }
    });

    setFormFieldValue(
      this.form,
      this.formFieldPath.virtualTransferItems,
      virtualTransferItems,
    );
    setFormFieldValue(
      this.form,
      this.formFieldPath.virtualReallyChangedTransfers,
      originReallyChangedTransfer,
      false,
    );

    const unsavedChangeInfo = getUnsavedChangeInfo(
      this.getFormFieldValue<ITransferData[]>(
        this.formFieldPath.virtualTransferItems,
      ),
      {
        i18nService: this.i18nService,
      },
    );

    this.transferDestinationsTitleTips$.next(unsavedChangeInfo.unsavedMessage);
  }

  public getBulkReplenishClickHandler(btn: ToolbarButtonDefinition): Function {
    return async (): Promise<void> => {
      const unsavedVirtualTransfers = this.getUnsavedDestinations();

      const btnReplenishConfig =
        BULK_REPLENISH_CONFIG_MAPPING[btn.caption as string];

      await this.replenishWorkerBuilder
        .build({
          additionDataOpts: {
            preInventory: [
              this.getUnsavedDataForReplenish.bind(this),
              this.getEventForReplenish.bind(this),
            ],
            afterInventory: btnReplenishConfig.afterInventory,
          },
          on: {
            beforeReplenish: this.beforeReplenish.bind(this),
            afterReplenish: this.afterReplenish.bind(this),
          },
        })
        .replenish({
          destination: map(unsavedVirtualTransfers, (transfer) => ({
            fromSite: this.getFormFieldValue(this.formFieldPath.siteId),
            fromSiteName: this.getFormFieldValue(this.formFieldPath.siteName),
            toSite: transfer.id,
            toSiteName: transfer.name,
            toSiteStoreNumber: transfer.store_number,
            inventory_items: transfer.inventory_items,
            percent: transfer.replenish_percent ?? DEFAULT_REPLENISH_PERCENT,
          })),
          strategy: btnReplenishConfig.strategy,
          messages: {
            noAvailable: this.i18nService.instant(
              I18n.ErrorMessages.BulkReplenishNoAvailableMessage,
              {
                type: REPLENISH_TYPE_NAMES.PAR,
              },
            ),
            error: this.i18nService.instant(
              I18n.InfoMessages.BulkReplenishFailedMessage,
            ),
            success: this.i18nService.instant(
              I18n.InfoMessages.BulkReplenishSuccessMessage,
              {
                replenishType: REPLENISH_TYPE_NAMES.PAR,
              },
            ),
          },
          confirmModalOptions: {
            modalTitle: btnReplenishConfig.modalTitle,
          },
        });
    };
  }

  /**
   * handle when list action
   *
   * @param action xux grid action
   * @param grid edit grid object
   */
  public handleDestinationGridAction(
    action: XuxGridAction,
    grid: XuxEditGridComponent,
  ): void {
    switch (action.type) {
      case XUX_GRID_ACTIONS.GRID_READY:
        this.destinationGrid = grid;
        break;
      case XUX_GRID_ACTIONS.ROW_DOUBLE_CLICKED:
        const selectedTransferData = head(
          action.payload.rowData as ITransferData[],
        );
        this.editSiteInventoryItems(selectedTransferData);
        break;
      default:
        return;
    }
  }

  public handleDestinationGridRemoveRow(
    action: XuxGridAction,
    grid: XuxEditGridComponent,
  ): void {
    const removedSiteIdToList = map(action.payload, 'site_id_to');

    if (isEmpty(removedSiteIdToList)) {
      return;
    }

    const reallyChangedTransfers: ReallyChangedTransfers =
      this.getFormFieldValue(this.formFieldPath.virtualReallyChangedTransfers);

    forEach(removedSiteIdToList, (siteIdTo) =>
      unset(reallyChangedTransfers, siteIdTo),
    );

    const unsavedChangeInfo = getUnsavedChangeInfo(
      this.getFormFieldValue<ITransferData[]>(
        this.formFieldPath.virtualTransferItems,
      ),
      {
        i18nService: this.i18nService,
      },
    );

    this.transferDestinationsTitleTips$.next(unsavedChangeInfo.unsavedMessage);

    setFormFieldValue(
      this.form,
      this.formFieldPath.virtualReallyChangedTransfers,
      reallyChangedTransfers,
      false,
    );
  }

  public handleGrandTotalGridReady(
    action: XuxGridAction,
    grid: XuxEditGridComponent,
  ): void {
    grid?.xuxGridComponent?.columnApi?.setColumnVisible(
      'received_cost',
      !isEqual(
        TRANSFER_TYPE.PRE_EVENT_TRANSFER,
        this.getFormFieldValue(this.formFieldPath.transferType),
      ),
    );
  }

  public async printOrExport(
    data: ITransferData,
    type: COMMON_ACTION_TYPES.Print | COMMON_ACTION_TYPES.Export,
  ): Promise<void> {
    set(
      data,
      TRANSFER_FORM_FIELD_PATHS.transferGroupName,
      get(this.form.value, this.formFieldPath.name, ''),
    );
    set(
      data,
      TRANSFER_FORM_FIELD_PATHS.virtualReallyChangedTransfers,
      get(this.form.value, this.formFieldPath.virtualReallyChangedTransfers),
    );
    const isNotFinalTransfer = this.transferPdfService.isNotFinalTransfer(
      data.status,
      data.transfer_type,
    );
    let modalColumns = isNotFinalTransfer
      ? [...TRANSFER_PDF_HEADERS]
      : [...TRANSFER_PDF_HEADERS_FINAL];
    if (!this.isQohFromVisible) {
      modalColumns = filter(
        modalColumns,
        (column) => !isEqual(column.path, 'qoh_from'),
      );
    }
    modalColumns = addParQtyColumn(modalColumns, data.status);
    modalColumns = addQtyPulledColumn(modalColumns, {
      transferStatus: data.status,
      preColumnPath: isNotFinalTransfer
        ? TRANSFER_PDF_HEADER_VALUES.Quantity
        : TRANSFER_PDF_HEADER_VALUES.InitialQuantity,
    });

    const totalFieldValue = !includes(
      [TRANSFER_STATUS_VALUE.Open, TRANSFER_STATUS_VALUE.Pending],
      data.status,
    );

    const pdfConfig = await this.transferService.getPdfConfig(
      async (definitions: IPdfDefinitions) => {
        const dmSyncResult = await this.taskService
          .getDmSyncResult([data], { isSync: true, noLoader: true })
          .toPromise();

        markTransferItemsAcknowledge(
          map(
            filter(map(dmSyncResult.sub_tasks, 'value'), 'is_acknowledge'),
            'transfer_id',
          ),
          this.form,
          this.formFieldPath,
        );

        return this.transferService.getPdfData(data, definitions?.options, {
          showQohFromOverride: this.isQohFromVisible,
          isGetUpdatedItems: false,
        });
      },
      modalColumns,
      null,
      {
        isTransferGroup: true,
        totalsConfiguration: {
          enabled: true,
          getDefaultValue: () => totalFieldValue,
        },
      },
    );
    if (type === COMMON_ACTION_TYPES.Print) {
      await this.transferGroupService.pdfGenerateServiceV2.printPdfAsync(
        pdfConfig,
      );
    } else {
      await this.transferGroupService.pdfGenerateServiceV2.downloadPdfAsync(
        pdfConfig,
      );
    }
  }

  public canPrintEmptyPdf(): boolean {
    if (!this.form) {
      return false;
    }
    const transfers = getFormFieldValue(
      this.form,
      this.formFieldPath.virtualTransferItems,
    );

    return (
      isEmpty(transfers) ||
      !some(transfers, (transfer) =>
        includes(
          [TRANSFER_STATUS_VALUE.Pending, TRANSFER_STATUS_VALUE.Open],
          transfer.status,
        ),
      )
    );
  }

  public canPrintPdfOrExport(): boolean {
    if (!this.form) {
      return false;
    }
    const transfers = getFormFieldValue(
      this.form,
      this.formFieldPath.virtualTransferItems,
    );

    return (
      isEmpty(transfers) ||
      every(transfers, (transfer) =>
        includes(TRANSFER_UNSAVED_STATUS, transfer.status),
      )
    );
  }

  public async refreshGrandTotal(
    param: IDocumentOperationOptions = {},
  ): Promise<void> {
    try {
      const transferGroupDetail =
        await this.transferGroupService.getDocumentByIdWithSkipUpdateDocument(
          this.document._id,
          merge(
            <IDocumentOperationOptions>{
              query: {
                include_transfer: true,
                transfer_fields_select:
                  this.transferGroupService.transferFieldsSelect.join(','),
              },
              skipMappers: true,
            },
            param,
          ),
        );

      // need update transfer_items in document for updateGrandTotalsData calc
      this.document = merge({}, this.document, {
        virtual: {
          transfer_items: transferGroupDetail?.transfer_items ?? [],
        },
      });

      this.updateGrandTotalsData(
        getFormFieldValue(this.form, this.formFieldPath.virtualTransferItems),
      );
    } catch (e) {}
  }

  public getPrintOrExportClickHandler(
    isWrittenSheet: boolean,
    isExport: boolean,
  ): Function {
    return async (selectedItems: ITransferData[]): Promise<void> => {
      const isSingle = isEqual(size(selectedItems), 1);
      const pdfGenerateFunc = this.transferService.bulkGenerateTransferSummary(
        isWrittenSheet,
        isExport,
        {
          isTransferGroup: true,
          printAndExportMaxLength: MAX_SITES_LIMIT,
          showQohFromOverride: this.isQohFromVisible,
        },
      );

      await pdfGenerateFunc(
        this.transferGroupService.addAdditionalInfoToTransfer(
          selectedItems,
          getFormFieldValue(this.form, this.formFieldPath.name),
          getFormFieldValue(
            this.form,
            this.formFieldPath.virtualReallyChangedTransfers,
          ),
        ),
        this.taskService.getPdfBulkDataWithSyncLoaderFunc(
          isSingle,
          this.form,
          this.formFieldPath,
        ),
      );
    };
  }

  public getDMSyncIconRendererParams(): Record<
    string,
    string | boolean | object
  > {
    return {
      isBusy: observableOf(false),
      dataSource: observableOf([]),
      valueMapper: ({ rowData, cellValue, params }): TooltipIconConfig[] => {
        if (isNil(rowData.updated_items) && isNil(rowData.unavailable_items)) {
          return [];
        }

        return [
          {
            icon: 'icon-warning',
            class: 'xvi-grid-warn-icon',
            tooltipConfig: {
              tooltip: this.i18nService.instant(
                I18n.InfoMessages.TransferGroupDMSyncTooltipMessage,
              ),
              container: 'xvi-grid-warn-icon',
              containerClass: 'flex-container',
            },
          },
        ];
      },
    };
  }

  // eslint-disable-next-line max-lines
}
